import type { ReactNode } from "react";
import { useRevealRows } from "../motion";
import { cx } from "./cx";
import { EmptyState } from "./EmptyState";
import { Skeleton } from "./Skeleton";
import styles from "./Table.module.css";

export type TableColumn<T> = {
  /** Identifiant de colonne, unique dans le tableau. Sert de clé React. */
  key: string;
  /** Intitulé affiché dans l'en-tête. */
  header: ReactNode;
  /** Contenu de la cellule pour une ligne. */
  render: (row: T, index: number) => ReactNode;
  /**
   * Colonne de montants : aligne à droite et passe les chiffres à chasse fixe.
   * À poser sur toute colonne de tokens, d'euros ou d'identifiants d'écriture.
   */
  numeric?: boolean;
  /** Alignement, quand `numeric` ne convient pas. Défaut `left`. */
  align?: "left" | "right" | "center";
  /** Largeur CSS de la colonne, ex. `"12rem"`. */
  width?: string;
};

export type TableProps<T> = {
  columns: TableColumn<T>[];
  rows: T[];
  /** Clé React de chaque ligne. Un identifiant du domaine, jamais l'index. */
  rowKey: (row: T, index: number) => string;
  /**
   * Résumé du tableau, rendu dans un `<caption>`. Obligatoire en pratique : un
   * lecteur d'écran annonce « tableau » sans dire de quoi.
   */
  caption: ReactNode;
  /** Chargement en cours : affiche des lignes fantômes à la place des données. */
  loading?: boolean;
  /** Nombre de lignes fantômes. Défaut `5`. */
  skeletonRows?: number;
  /** Rendu quand `rows` est vide et que le chargement est fini. */
  empty?: ReactNode;
  /**
   * Révèle les lignes au défilement, par lots (`ScrollTrigger.batch`). Défaut
   * `true`. À passer à `false` pour un tableau court, toujours entièrement
   * visible : la révélation n'y apporterait rien.
   */
  animateRows?: boolean;
  /** Ligne cliquable. Rend la ligne focalisable et actionnable au clavier. */
  onRowClick?: (row: T, index: number) => void;
  /** Bandeau bas : pagination, total, bouton « charger la suite ». */
  footer?: ReactNode;
  className?: string;
};

/**
 * Tableau de données.
 *
 * Prend en charge les trois états d'un écran — chargement (lignes fantômes),
 * vide (`empty`), rempli — pour qu'aucun des cinq écrans n'ait à les recoder.
 */
export function Table<T>({
  columns,
  rows,
  rowKey,
  caption,
  loading = false,
  skeletonRows = 5,
  empty,
  animateRows = true,
  onRowClick,
  footer,
  className,
}: TableProps<T>) {
  const corps = useRevealRows<HTMLTableSectionElement>({
    dependencies: [animateRows && !loading ? rows : null],
  });

  const vide = !loading && rows.length === 0;

  return (
    <div className={cx(styles.enveloppe, className)}>
      <table className={styles.tableau}>
        <caption className={styles.legende}>{caption}</caption>
        <thead>
          <tr>
            {columns.map((colonne) => (
              <th
                key={colonne.key}
                scope="col"
                style={colonne.width ? { width: colonne.width } : undefined}
                className={cx(
                  colonne.numeric && styles.numerique,
                  colonne.align === "right" && styles.right,
                  colonne.align === "center" && styles.center,
                )}
              >
                {colonne.header}
              </th>
            ))}
          </tr>
        </thead>

        <tbody ref={animateRows && !loading ? corps : undefined} aria-busy={loading || undefined}>
          {loading &&
            Array.from({ length: skeletonRows }, (_, i) => (
              <tr key={`squelette-${i}`}>
                {columns.map((colonne) => (
                  <td key={colonne.key}>
                    <Skeleton />
                  </td>
                ))}
              </tr>
            ))}

          {!loading &&
            rows.map((ligne, index) => (
              <tr
                key={rowKey(ligne, index)}
                data-anim={animateRows ? "row" : undefined}
                className={cx(onRowClick && styles.cliquable)}
                tabIndex={onRowClick ? 0 : undefined}
                onClick={onRowClick ? () => onRowClick(ligne, index) : undefined}
                onKeyDown={
                  onRowClick
                    ? (e) => {
                        // Une ligne cliquable doit s'actionner au clavier :
                        // sans ça, la moitié du tableau est hors d'atteinte.
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          onRowClick(ligne, index);
                        }
                      }
                    : undefined
                }
              >
                {columns.map((colonne) => (
                  <td
                    key={colonne.key}
                    className={cx(
                      colonne.numeric && styles.numerique,
                      colonne.align === "right" && styles.right,
                      colonne.align === "center" && styles.center,
                    )}
                    // L'intitulé recopié sur la cellule : en largeur réduite,
                    // chaque ligne devient une fiche et la cellule le rappelle.
                    data-label={typeof colonne.header === "string" ? colonne.header : undefined}
                  >
                    {colonne.render(ligne, index)}
                  </td>
                ))}
              </tr>
            ))}
        </tbody>
      </table>

      {vide && (
        <div className={styles.zoneVide}>
          {empty ?? <EmptyState compact title="Aucune donnée à afficher." />}
        </div>
      )}

      {footer !== undefined && <div className={styles.pied}>{footer}</div>}
    </div>
  );
}
